import { createLogger } from '@/lib/logger'

import { refreshAccessToken } from './api-client'

/**
 * Proactive token refresh scheduler.
 *
 * Cognito access tokens live for 1 hour. Refreshing on a fixed cadence while
 * the user is authenticated means requests never race an expired token (no
 * 401 bursts after sleep/idle), and every refresh re-sets the sliding
 * refresh-token cookie so long-lived sessions keep extending.
 *
 * Refreshes are also triggered when the tab becomes visible or the network
 * comes back after the session has gone stale — a laptop waking from sleep
 * misses interval ticks, so these events are the reliable wake-up signal.
 *
 * @module lib/api/token-refresh-scheduler
 */

const logger = createLogger('lib/api/token-refresh-scheduler')

// Refresh well before the 1-hour access token expiry
const REFRESH_INTERVAL_MS = 45 * 60 * 1000
const STALE_THRESHOLD_MS = REFRESH_INTERVAL_MS

let intervalId: number | null = null
let lastRefreshAt = 0
let isSchedulerRefreshInProgress = false

async function runScheduledRefresh(trigger: string): Promise<void> {
  if (isSchedulerRefreshInProgress) return
  isSchedulerRefreshInProgress = true
  try {
    logger.debug(`[TokenRefreshScheduler] Refreshing tokens (${trigger})`)
    const success = await refreshAccessToken()
    if (success) {
      lastRefreshAt = Date.now()
    } else {
      // A definitive failure will surface on the next real request via the
      // 401 handling in apiClient; the scheduler never forces a logout.
      logger.warn(`[TokenRefreshScheduler] Refresh failed (${trigger})`)
    }
  } catch (error) {
    logger.warn('[TokenRefreshScheduler] Refresh error:', error)
  } finally {
    isSchedulerRefreshInProgress = false
  }
}

function refreshIfStale(trigger: string): void {
  if (Date.now() - lastRefreshAt >= STALE_THRESHOLD_MS) {
    void runScheduledRefresh(trigger)
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    refreshIfStale('visibilitychange')
  }
}

function handleOnline(): void {
  refreshIfStale('online')
}

export function startTokenRefreshScheduler(): void {
  if (intervalId !== null) return

  // The caller just authenticated (or an auth check just passed), so the
  // access token is fresh — start the clock now instead of refreshing.
  lastRefreshAt = Date.now()
  intervalId = window.setInterval(() => {
    void runScheduledRefresh('interval')
  }, REFRESH_INTERVAL_MS)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('online', handleOnline)
  logger.debug('[TokenRefreshScheduler] Started')
}

export function stopTokenRefreshScheduler(): void {
  if (intervalId !== null) {
    window.clearInterval(intervalId)
    intervalId = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('online', handleOnline)
  logger.debug('[TokenRefreshScheduler] Stopped')
}
